import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Minus, Plus, ShoppingCart, ShieldCheck, Truck, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { API, imgSrc, gbp } from '../lib/shop';

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [status, setStatus] = useState('loading');
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setStatus('loading');
    axios.get(`${API}/shop/products/${slug}`)
      .then(r => { setProduct(r.data); setStatus('ok'); })
      .catch(() => setStatus('missing'));
  }, [slug]);

  if (status === 'loading') return <div className="min-h-screen pt-32 text-center text-gray-600">Loading...</div>;
  if (status === 'missing') {
    return (
      <div className="min-h-screen pt-32 text-center px-4" data-testid="product-not-found">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Product not found</h1>
        <Link to="/shop" className="text-pink-600 hover:underline">Back to shop</Link>
      </div>
    );
  }

  const soldOut = product.stock !== null && product.stock !== undefined && product.stock <= 0;
  const add = () => { addItem(product, qty); toast.success(`${qty} × ${product.name} added to cart`); };
  const buyNow = () => { addItem(product, qty); navigate('/checkout'); };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-white" data-testid="product-page">
      <div className="container mx-auto px-4">
        <Link to="/shop" className="inline-flex items-center text-sm text-gray-600 hover:text-pink-600 mb-8" data-testid="back-to-shop">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to shop
        </Link>
        <div className="grid md:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div className="rounded-3xl overflow-hidden bg-pink-50/40 border-2 border-pink-50 aspect-square">
            <img src={imgSrc(product.image_url)} alt={product.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-pink-600 font-semibold mb-2">{product.category}</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight" data-testid="product-name">{product.name}</h1>
            <p className="text-3xl font-bold text-gray-900 mb-6" data-testid="product-price">{gbp(product.price)}</p>
            <p className="text-base text-gray-700 leading-relaxed mb-8 whitespace-pre-line" data-testid="product-description">{product.description}</p>

            {product.stock !== null && product.stock !== undefined && (
              <p className={`text-sm font-medium mb-4 ${soldOut ? 'text-red-600' : 'text-green-700'}`} data-testid="product-stock">
                {soldOut ? 'Currently sold out' : `${product.stock} in stock`}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4 mb-8">
              <div className="inline-flex items-center border-2 border-gray-200 rounded-full">
                <button onClick={() => setQty(q => Math.max(1, q - 1))} className="p-3 hover:text-pink-600" aria-label="Decrease quantity" data-testid="qty-decrease"><Minus className="w-4 h-4" /></button>
                <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(q => Math.min(50, q + 1))} className="p-3 hover:text-pink-600" aria-label="Increase quantity" data-testid="qty-increase"><Plus className="w-4 h-4" /></button>
              </div>
              <Button onClick={add} disabled={soldOut} size="lg" data-testid="product-add-to-cart" className="bg-pink-500 hover:bg-pink-600 text-white rounded-full">
                <ShoppingCart className="w-5 h-5 mr-2" /> Add to cart
              </Button>
              <Button onClick={buyNow} disabled={soldOut} size="lg" variant="outline" data-testid="product-buy-now" className="border-pink-500 text-pink-600 hover:bg-pink-50 rounded-full">
                Buy now
              </Button>
            </div>

            <ul className="grid sm:grid-cols-3 gap-4 text-sm text-gray-600">
              <li className="flex items-start gap-2"><CreditCard className="w-5 h-5 text-pink-500 shrink-0" /> Secure card payment via Stripe</li>
              <li className="flex items-start gap-2"><Truck className="w-5 h-5 text-pink-500 shrink-0" /> Delivery across Peterborough & UK</li>
              <li className="flex items-start gap-2"><ShieldCheck className="w-5 h-5 text-pink-500 shrink-0" /> Used by our own cleaning team</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
